import SubmissionService from '@/core/collections/submission/submission.service';
import i18n from '@/i18n';
import { NotificationType } from '@nxtpeople/nxt-styles';
import { Module } from 'vuex';
const submissionService = new SubmissionService();

const SubmissionStore: Module<any, any> = {
   namespaced: true,
   state: {
      states: [],
      selected_ids: [],
      submissions: [],
   },
   getters: {
      areAllSelected(state) {
         return state.submissions.length == state.selected_ids.length;
      },
   },
   mutations: {
      SET_STATES(state, value) {
         state.states = value;
      },
      SET_SELECTED_ITEMS(state, value) {
         if (state.selected_ids.includes(value)) {
            const SUBMISSION_ID_INDEX = state.selected_ids.indexOf(value);
            if (SUBMISSION_ID_INDEX > -1) {
               state.selected_ids.splice(SUBMISSION_ID_INDEX, 1);
            }
         } else {
            state.selected_ids.push(value);
         }
      },
      SELECT_ALL(state) {
         state.selected_ids = state.submissions.map((submission: any) => submission.id);
      },
      UNSELECT_ALL(state) {
         state.selected_ids = [];
      },
      CLEAR_SUBMISSIONS(state) {
         state.submissions = [];
      },
      SET_SUBMISSIONS(state, value) {
         state.submissions = [...state.submissions, ...value];
      },
      UPDATE_SUBMISSION_TAGS(state, { submission_id, tags }) {
         const SUBMISSION = state.submissions.find((submission: any) => submission.id == submission_id);
         if (SUBMISSION) SUBMISSION.tags = tags;
      },
      UPDATE_SUBMISSION_CANDIDATE_STEP(state, { submission_id, updated_candidate_step, score, last_submission_state_id }) {
         const SUBMISSION = state.submissions.find((submission: any) => submission.id == submission_id);
         if (!SUBMISSION || !updated_candidate_step) return;

         const candidateStepIndex = SUBMISSION.candidate_steps.findIndex(
            (candidate_step: any) => candidate_step.id == updated_candidate_step.id
         );
         if (candidateStepIndex === -1) return;

         SUBMISSION.candidate_steps.splice(candidateStepIndex, 1, updated_candidate_step);
         if (score !== undefined) SUBMISSION.score = score;
         if (last_submission_state_id !== undefined) SUBMISSION.last_submission_state_id = last_submission_state_id;
      },
      REMOVE_SUBMISSION(state, { submission_id }) {
         const INDEX = state.submissions.findIndex((submission: any) => submission.id == submission_id);
         if (INDEX > -1) state.submissions.splice(INDEX, 1);
         this.commit('submission/REMOVE_SELECTED_SUBMISSION_ID', { submission_id });
      },
      REMOVE_SELECTED_SUBMISSION_ID(state, { submission_id }) {
         const INDEX = state.selected_ids.findIndex((id: any) => id == submission_id);
         if (INDEX > -1) state.selected_ids.splice(INDEX, 1);
      },
      ADD_ANSWER_TO_CANDIDATE_STEP(state, { submission_id, candidate_step_id, updated_answer }) {
         const SUBMISSION = state.submissions.find((submission: any) => submission.id == submission_id);
         if (!SUBMISSION) return;

         const CANDIDATE_STEP = SUBMISSION.candidate_steps.find((candidate_step: any) => candidate_step.id == candidate_step_id);
         if (!CANDIDATE_STEP) return;

         CANDIDATE_STEP.answers.push(updated_answer);
      },
      REMOVE_ANSWER_FROM_CANDIDATE_STEP(state, { submission_id, candidate_step_id, answer_id }) {
         const SUBMISSION = state.submissions.find((submission: any) => submission.id == submission_id);
         if (!SUBMISSION) return;

         const CANDIDATE_STEP = SUBMISSION.candidate_steps.find((candidate_step: any) => candidate_step.id == candidate_step_id);
         if (!CANDIDATE_STEP) return;

         const ANSWER_INDEX = CANDIDATE_STEP.answers.findIndex((answer: any) => answer.id == answer_id);
         if (ANSWER_INDEX > -1) CANDIDATE_STEP.answers.splice(ANSWER_INDEX, 1);
      },
      UPDATE_SUBMISSION_ANSWER(state, { updated_answer }) {
         const SUBMISSION = state.submissions.find((submission: any) => submission.id == updated_answer.candidate_step.submission_id);
         if (!SUBMISSION) return;

         const CANDIDATE_STEP = SUBMISSION.candidate_steps.find(
            (candidate_step: any) => candidate_step.id == updated_answer.candidate_step_id
         );
         if (!CANDIDATE_STEP) return;

         const ANSWER_INDEX = CANDIDATE_STEP.answers.findIndex((answer: any) => answer.id == updated_answer.id);
         if (ANSWER_INDEX > -1) CANDIDATE_STEP.answers.splice(ANSWER_INDEX, 1, updated_answer);
      },
   },
   actions: {
      async getSetSubmissionStates(context) {
         return await submissionService.getSubmissionStates().then((response) => {
            response.forEach((state: any) => {
               state.hidden = !state.hidden;
            });
            context.commit('SET_STATES', response);
            return response;
         });
      },
      async sendToNxtPlan(context, { submission_id }) {
         return await submissionService.sendToNxtPlan(submission_id).then((response) => {
            context.dispatch('addNotification', { notification: { type: 'info', title: response?.message } }, { root: true });
         });
      },
      async postSubmissionNotification(context, { submissionId, mediumType, notificationId }) {
         let body = {
            submission_id: submissionId,
            notification_medium_type: mediumType,
            notification_id: notificationId,
         };
         const medium = mediumType == 'email' ? i18n.global.t('email') : i18n.global.t('sms');

         return await submissionService
            .postSubmissionNotification(body)
            .then((response) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: 'success',
                        title: i18n.global.t('medium_sent', { medium }),
                     },
                  },
                  { root: true }
               );
            })
            .catch((error) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: NotificationType.Danger,
                        title: i18n.global.t('medium_not_sent', { medium }),
                     },
                  },
                  { root: true }
               );
            });
      },
      async getSubmissionHistory(context, { id }) {
         return await submissionService.getSubmissionHistory(id).then((response) => {
            return response;
         });
      },
      async postSubmissionOccurence(context, { submissionId, comment }) {
         return await submissionService.postSubmissionOccurence(submissionId, comment).then((response) => {
            return response;
         });
      },
      async patchSubmissionById(context, patch) {
         let body = [
            {
               op: 'replace',
               path: patch.path,
               value: patch.value,
            },
         ];
         return await submissionService.patchSubmissionById({ id: patch.id, body: body }).then((response) => {
            return response;
         });
      },
      async getSubmissions(context, { query }) {
         const RESULT = await submissionService.getSubmissions(query);

         context.commit('SET_SUBMISSIONS', RESULT.data);
         return RESULT;
      },
      async getSubmissionById(context, { id, cancelToken }) {
         return await submissionService.getSubmissionById(id, cancelToken).then((response) => {
            return response;
         });
      },
      async deleteSubmission(context, { id }) {
         return await submissionService
            .deleteSubmission(id)
            .then((response) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },
      async forceDeleteSubmission(context, { id }) {
         return await submissionService
            .forceDeleteSubmission(id)
            .then((response) => {
               return response;
            })
            .catch((error) => {
               throw error;
            });
      },
      async postFakeRemindEmail(context, { journeyId }) {
         return await submissionService
            .postFakeRemindEmail(context.rootGetters.journeyLanguage.code, journeyId)
            .then((response) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: 'success',
                        title: i18n.global.t('email_sent'),
                     },
                  },
                  { root: true }
               );
               return response;
            })
            .catch((error) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: NotificationType.Danger,
                        title: i18n.global.t('email_not_sent'),
                     },
                  },
                  { root: true }
               );
               throw error;
            });
      },
      async postFakeEndEmail(context, { journeyId, type }) {
         return await submissionService
            .postFakeEndEmail(context.rootGetters.journeyLanguage.code, journeyId, type)
            .then((response) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: 'success',
                        title: i18n.global.t('email_sent'),
                     },
                  },
                  { root: true }
               );
               return response;
            })
            .catch((error) => {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: NotificationType.Danger,
                        title: i18n.global.t('email_not_sent'),
                     },
                  },
                  { root: true }
               );
               throw error;
            });
      },
      async getTagsByReferralId(context, { submissionIds }: { submissionIds: Array<String | Number> }) {
         return await submissionService.getTagsByReferralId(submissionIds);
      },
      async sendToNxtPlanInBulk({}, { body }) {
         return await submissionService.sendToNxtPlanInBulk(body).catch((error: any) => {
            return Promise.reject(error);
         });
      },
      async fetchAllowedOrNot({}, { submission_ids }) {
         const BODY = {
            submission_ids: submission_ids.join(','),
         };
         return await submissionService.fetchAllowedOrNot(BODY);
      },
      async findPeople(context, { submissionId }) {
         return await submissionService.findPeople(submissionId);
      },
      async getNxtCandidateSubmission(context, { candidate_sync_id }) {
         return await submissionService.getNxtCandidateSubmission(candidate_sync_id).then((response) => {
            return response;
         });
      },
      async exportSubmissions(context, { submission_ids, language_key }) {
         return await submissionService.exportSubmissions({ submission_ids, language_key }).then((response) => {
            return response;
         });
      },
   },
};

export default SubmissionStore;
